import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { FolderPlus, Search, Check, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import type { D2DArea } from '@/types/d2d';

interface AreaFolderDialogProps {
  areas: D2DArea[];
  onCreateFolder: (folderName: string, areaIds: string[]) => Promise<void>;
}

export const AreaFolderDialog = ({ areas, onCreateFolder }: AreaFolderDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'name' | 'select'>('name');
  const [folderName, setFolderName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAreaIds, setSelectedAreaIds] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const resetDialog = () => {
    setStep('name');
    setFolderName('');
    setSearchQuery('');
    setSelectedAreaIds([]);
    setIsCreating(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      resetDialog();
    }
  };

  const handleNext = () => {
    if (folderName.trim()) {
      setStep('select');
    }
  };

  const handleBack = () => {
    setStep('name');
  };

  const handleToggleArea = (areaId: string) => {
    setSelectedAreaIds(prev => 
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const handleCreate = async () => {
    if (selectedAreaIds.length === 0 || !folderName.trim()) return;
    
    setIsCreating(true);
    try {
      await onCreateFolder(folderName.trim(), selectedAreaIds);
      handleOpenChange(false);
    } catch (error) {
      console.error('Error creating folder:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // Filter areas based on search query (PLZ or name)
  const filteredAreas = areas.filter(area => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase().trim();
    const nameMatch = area.name.toLowerCase().includes(query);
    const plzMatch = area.plz?.toLowerCase().includes(query);
    
    return nameMatch || plzMatch;
  });

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2 w-full hover:bg-transparent">
          <FolderPlus className="h-4 w-4" />
          Gebiete ordnen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === 'name' ? 'Ordner erstellen' : 'Gebiete hinzufügen'}
          </DialogTitle>
        </DialogHeader>

        {step === 'name' ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Ordnername</Label>
              <Input
                id="folder-name"
                placeholder="z.B. Südstadt, Karlsruhe West..."
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleNext}
                disabled={!folderName.trim()}
                className="gap-2"
              >
                Weiter
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FolderPlus className="h-4 w-4" />
              <span>Ordner: <strong className="text-foreground">{folderName}</strong></span>
            </div>
            
            <Separator />
            
            <div className="space-y-2">
              <Label htmlFor="area-search">Gebiete suchen</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="area-search"
                  placeholder="PLZ oder Name eingeben..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {filteredAreas.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  {searchQuery ? 'Keine Gebiete gefunden' : 'Keine Gebiete vorhanden'}
                </p>
              ) : (
                filteredAreas.map((area) => (
                  <div
                    key={area.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedAreaIds.includes(area.id)
                        ? 'bg-primary/10 border-primary'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleToggleArea(area.id)}
                  >
                    <Checkbox
                      checked={selectedAreaIds.includes(area.id)}
                      onCheckedChange={() => handleToggleArea(area.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{area.name}</p>
                      {area.plz && (
                        <p className="text-sm text-muted-foreground">{area.plz}</p>
                      )}
                      {area.description && !area.description.includes('Migriert aus altem System') && (
                        <p className="text-xs text-muted-foreground/70 truncate">{area.description}</p>
                      )}
                    </div>
                    {selectedAreaIds.includes(area.id) && (
                      <Check className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </div>
                ))
              )}
            </div>

            {selectedAreaIds.length > 0 && (
              <p className="text-sm text-muted-foreground text-center">
                {selectedAreaIds.length} {selectedAreaIds.length === 1 ? 'Gebiet' : 'Gebiete'} ausgewählt
              </p>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleBack}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </Button>
              <Button
                onClick={handleCreate}
                disabled={selectedAreaIds.length === 0 || isCreating}
                className="flex-1 gap-2"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Wird erstellt...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Hinzufügen ({selectedAreaIds.length})
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
